import { useQuery } from '@tanstack/react-query';

import { useAuthOptional } from '@/context/AuthContext';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import { SETTINGS_MODULE_FLAGS } from '@/lib/settingsPaths';
import type { AccountRecordPatch, AccountUnboundField } from '@/pages/account/accountSource';

/**
 * AR-391 — where the Settings module's data comes from.
 *
 * AR-390 built the four pages against the Account center's sample record and
 * left the swap for here. With the module's flags on, `GET /api/settings-module`
 * returns the member's own AI writing profile and Sites account, and they are
 * laid over the sample record by the SAME merge the Account center uses
 * (`mergeAccountRecord`) — one rule for both columns of the one record.
 *
 * The three Color Theme settings are NOT on this payload. They are the member
 * record's `color_theme / display_mode / text_size` (AR-393), read by
 * ThemeContext from `/api/user/preferences` and applied before first paint, so
 * the page reads them from `useTheme()` directly.
 *
 * Gated on `SETTINGS_MODULE_FLAGS` — the pair that shows the module at all.
 * There is one switch for the module and its data, not two: off, the module
 * is not shown and nothing is fetched; on, every bound field reads the
 * migrated model.
 */

/** The member's WorldVia Sites website, from `user_website_accounts`. */
export interface SettingsSites {
  /** Whether a Sites row exists — the has-account / no-account switch. */
  hasAccount: boolean;
  status: string;
  domain: string;
  url: string;
  plan: string;
  /** Monthly, from the Stripe subscription; null when it could not be read. */
  price: number | null;
}

export interface SettingsModulePayload {
  record: AccountRecordPatch;
  sites: SettingsSites;
  unbound: AccountUnboundField[];
}

export function useSettingsSource() {
  const auth = useAuthOptional();
  const userId = auth?.user?.id ?? null;
  const { isEnabled, isLoading: flagsLoading } = useResolvedFeatureFlags();
  const enabled = SETTINGS_MODULE_FLAGS.every((flag) => isEnabled(flag));

  const { data, isLoading, error } = useQuery<SettingsModulePayload>({
    // Keyed by user for the same reason the Account center's is: the local
    // auth bypass swaps the member without a logout, and one member's
    // writing samples must never show for the next.
    queryKey: ['/api/settings-module', userId],
    queryFn: async () => {
      const response = await fetch('/api/settings-module', { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Failed to load the Settings module record (${response.status})`);
      }
      return response.json();
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    // A bound field is read-only in this ticket, so a background refetch
    // would only replace what the member is looking at.
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    payload: data,
    /** True once the pages show the member's own settings rather than the sample. */
    isLive: Boolean(data),
    isLoading: flagsLoading || (enabled && isLoading),
    error,
  };
}
